import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Radio, LayoutGrid, UploadCloud, Vault, Users, Hourglass, Globe, Tag, ShieldCheck, LogOut, Menu, X, Telescope } from "lucide-react";

const NavItem = ({ to, label, icon: Icon, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 font-mono-data text-[11px] tracking-[0.18em] uppercase transition-all border-l-2 ${
        isActive ? "border-[#00D4FF] text-white bg-white/[0.03]" : "border-transparent text-white/55 hover:text-white hover:border-white/30"
      }`
    }
  >
    <Icon size={14} className="opacity-90" />
    <span>{label}</span>
  </NavLink>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 holo-panel border-r border-white/5 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/s2s-logo-sm.png" alt="S2S — Sounds To Space"
                 className="h-9 w-auto select-none pointer-events-none"
                 style={{ filter: "drop-shadow(0 0 10px rgba(0,212,255,0.4))" }} />
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-white/60" data-testid="sidebar-close">
            <X size={18} />
          </button>
        </div>
        <nav className="p-3 space-y-1">
          <NavItem to="/dashboard" label={t("nav.dashboard")} icon={LayoutGrid} testid="nav-dashboard" />
          <NavItem to="/galaxy" label={t("nav.galaxy")} icon={Telescope} testid="nav-galaxy" />
          <NavItem to="/upload" label={t("nav.upload")} icon={UploadCloud} testid="nav-upload" />
          <NavItem to="/vault" label={t("nav.vault")} icon={Vault} testid="nav-vault" />
          <NavItem to="/family" label={t("nav.family")} icon={Users} testid="nav-family" />
          <NavItem to="/legacy" label={t("nav.legacy")} icon={Hourglass} testid="nav-legacy" />
          <NavItem to="/globe" label={t("nav.globe")} icon={Globe} testid="nav-globe" />
          <NavItem to="/pricing" label={t("nav.pricing")} icon={Tag} testid="nav-pricing" />
          {user?.role === "admin" && (
            <NavItem to="/admin" label={t("nav.admin")} icon={ShieldCheck} testid="nav-admin" />
          )}
        </nav>
        <div className="p-4 mt-4 border-t border-white/5">
          <div className="label-overline mb-2">Operator</div>
          <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="font-mono-data text-[11px] text-white/55 truncate">{user?.email}</div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 border border-white/15 hover:border-white/40 text-[11px] font-mono-data tracking-[0.18em] uppercase"
          >
            <LogOut size={12} /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 holo-panel border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden text-white/70" data-testid="sidebar-open">
              <Menu size={20} />
            </button>
            <Radio size={16} className="text-[#00D4FF] blink" />
            <span className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/70">
              LINK · {new Date().toUTCString().replace(/^\w+, /, "")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 grid-bg">{children}</main>
      </div>
    </div>
  );
}
